/**
 * @description HeatmapHelpers module
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module helpers/HeatmapHelpers
 */

import CONSTANTS from "../config/Constants";

/**
 * @description Sets a specified period for the current day visible.
 * @export
 * @param {string} periodStart - A string representation of the start timeBin.
 * @param {array} routesPeriods - The routesPeriods to update.
 * @returns {array} The updated routesPeriods.
 */
export function setPeriodVisibility(periodStart, routesPeriods) {
    const clonedRoutesPeriods = cloneObject(routesPeriods),
        todaysRoutesPeriods = clonedRoutesPeriods.find(
            day =>
                getDateStringFromDate(new Date(day.date)) ===
                getDateStringFromDate(new Date())
        );

    todaysRoutesPeriods &&
        todaysRoutesPeriods.carriageways.forEach(carriageway => {
            const period = carriageway.periods.find(
                period =>
                    period.timeframe === convertTimeBinToTimeframe(periodStart)
            );

            if (period) {
                period.isVisible = true;
            }
        });

    return clonedRoutesPeriods;
}

/**
 * @description Returns an object containing lists of all junctions and links from all routes.
 * @export
 * @param {array} routes - An array of routes.
 * @returns {Object} - The junctions and links data.
 */
export function getJunctionsAndLinksFromRoutes(routes) {
    const data = {
        junctions: [],
        links: []
    };

    routes.forEach(route =>
        route.carriageways.forEach(carriageway => {
            data.junctions = [
                ...data.junctions,
                ...carriageway.junctions.map(junction => {
                    return {
                        ...junction,
                        carriageway: carriageway.id
                    };
                })
            ];
            data.links = [
                ...data.links,
                ...carriageway.links.map(link => {
                    return {
                        ...link,
                        carriageway: carriageway.id
                    };
                })
            ];
        })
    );

    return data;
}

/**
 * @description Expands a heatmap by the specified carriageway id.
 * @export
 * @param {Object} expandedRoutes - The route data in which to find the carriageway.
 * @param {string} carriagewayId - The carriageway id.
 * @param {boolean} state - The specified state. Will toggle if unspecified.
 * @returns {object} The updated route data.
 */
export function expandHeatmapByCarriagewayId(
    expandedRoutes,
    carriagewayId,
    state
) {
    const clonedExpandedRoutes = cloneObject(expandedRoutes),
        routeIndex = clonedExpandedRoutes.findIndex(id => id === carriagewayId);

    if (
        (routeIndex > -1 && state === true) ||
        (routeIndex === -1 && state === false)
    ) {
        // No change to expansion.
        return clonedExpandedRoutes;
    }

    if (routeIndex > -1) {
        // Remove from expanded routes
        clonedExpandedRoutes.splice(routeIndex, 1);
    } else {
        // Add to expanded routes
        clonedExpandedRoutes.push(carriagewayId);
    }

    return clonedExpandedRoutes;
}

/**
 * @description Sets the isSelected flag on selected routes.
 * @export
 * @param {array} routeData - The route data.
 * @param {array} [selectedCarriageways=[]] - The list of carriageways or entire routes to select.
 * @returns {array} - The modified data.
 */
export function setSelectedRoutes(routeData, selectedCarriageways = []) {
    const filteredRouteData = cloneObject(routeData),
        selectedRoutes = selectedCarriageways.filter(
            carriageway => carriageway.split("_").length === 1
        );

    filteredRouteData.forEach(route => {
        route.carriageways = route.carriageways.map(carriageway => {
            return {
                ...carriageway,
                isSelected:
                    selectedCarriageways.indexOf(carriageway.id) >= 0 ||
                    selectedRoutes.indexOf(route.name) >= 0
            };
        });
        const selectedRouteCarriageways = route.carriageways.filter(
            carriageway => carriageway.isSelected
        ).length;

        route.isSelected =
            selectedRoutes.indexOf(route.name) >= 0 ||
            selectedRouteCarriageways === route.carriageways.length
                ? 1
                : selectedRouteCarriageways.length > 0
                ? 0
                : -1;
    });

    return filteredRouteData;
}

/**
 * @description Sets the junction and link visibility.
 * @export
 * @param {Object} routeData - The route data.
 * @param {string | number} junctionFrom - The junction to filter from.
 * @param {string | number} junctionTo - The junction to filter to.
 * @returns {object} The filtered route data.
 */
export function getVisibleJunctionsAndLinks(
    routeData,
    junctionFrom,
    junctionTo
) {
    let visibleJunctions = [],
        visibleLinks = [];
    const route = cloneObject(routeData),
        filteredJunctions = route.junctions.filter(
            (junction, index) => index >= junctionFrom && index <= junctionTo
        );

    route.carriageways.forEach(carriageway => {
        const carriagewayJunctions = getVisibleJunctions(
            carriageway,
            filteredJunctions
        );

        visibleJunctions = [...visibleJunctions, ...carriagewayJunctions];
        visibleLinks = [
            ...visibleLinks,
            ...getVisibleLinks(carriageway, carriagewayJunctions)
        ];
    });

    return {
        junctions: visibleJunctions,
        links: visibleLinks
    };
}

/**
 * @description Sets the value of the CSS vars that relate to delay colours.
 * @export
 * @param {array} selectedDelays - The selected delays.
 * @param {array} delays - The delay values.
 */
export function setHeatmapColours(selectedDelays, delays) {
    const root = document.documentElement;

    delays.forEach((delay, index) => {
        const colour = delay.colour;

        root.style.setProperty(
            `--delay-colour-${index}`,
            selectedDelays[index]
                ? `rgb(${colour.red},${colour.green},${colour.blue})`
                : "#ffffff"
        );
    });
}

/**
 * @description Creates empty time bins for the specified date.
 * @param {string} date - A string representing the date.
 * @returns {array} - An array of empty periods.
 */
function generateEmptyPeriods(date) {
    const today = new Date(),
        selectedDate = new Date(date),
        isToday =
            getDateStringFromDate(today) ===
            getDateStringFromDate(selectedDate);

    if (isToday) {
        if (today.getHours() === 0 && today.getMinutes() < CONSTANTS.UPDATE_INTERVAL_MINS) {
            selectedDate.setMinutes(0, 0, 0);
        } else {
            selectedDate.setHours(
                today.getHours(), // Current hour
                Math.floor(today.getMinutes() / CONSTANTS.UPDATE_INTERVAL_MINS) *
                    CONSTANTS.UPDATE_INTERVAL_MINS -
                    CONSTANTS.UPDATE_INTERVAL_MINS, // Round minutes down to start of previous timeBin
                0,
                0
            );
        }        
    } else {
        selectedDate.setHours(23, 45, 0, 0);
    }

    const finalTimeframe = convertTimeBinToTimeframe(selectedDate),
        timeframeIterator = timeframeGenerator(),
        periods = [];
    let timeframe = timeframeIterator.next(),
        isVisible = finalTimeframe !== timeframe.value;

    while (!timeframe.done) {
        const timeframeValue = timeframe.value;

        isVisible = isVisible && finalTimeframe !== timeframe.value;

        periods.unshift({
            timeframe: timeframeValue,
            isVisible: isVisible
        });
        timeframe = timeframeIterator.next();
    }

    return periods;
}

/**
 * @description Adds a new period to the correct route within the selected routes.
 * @param {Object} period - The new period to add.
 * @param {array} selectedRoutes - The selected routes.
 * @returns {array} The selected routes with the added period.
 */
function addNewPeriodToCarriageway(carriageway, existingDatePeriods) {
    let existingCarriageway = existingDatePeriods.carriageways.find(
        datePeriods => datePeriods.id === carriageway.id
    );

    if (!existingCarriageway) {
        existingCarriageway = {
            id: carriageway.id,
            periods: generateEmptyPeriods(existingDatePeriods.date)
        };
        existingDatePeriods.carriageways.push(existingCarriageway);
    }

    const routePeriods = existingCarriageway.periods.map(period => {
        const existingPeriod = carriageway.periods.find(
            carriagewayPeriod =>
                period.timeframe === carriagewayPeriod.timeframe
        );

        if (existingPeriod) {
            return {
                ...existingPeriod,
                isVisible: true
            };
        }

        return period;
    });

    existingCarriageway.periods = routePeriods.sort(
        (a, b) =>
            new Date("1970/01/01 " + b.timeframe.split(" ")[0]) -
            new Date("1970/01/01 " + a.timeframe.split(" ")[0])
    );
}

/**
 * @description Fills gaps within timeBin journeyTime data and ensures journeyTimes are in the correct order.
 * @param {array} carriagewayData - The new carriageway data to fill.
 * @param {array} carriagewayLinks - The links for the given carriageway.
 * @returns {array} - The filled data.
 */
function fillTimeBinGaps(carriagewayData, carriagewayLinks) {
    carriagewayData.periods.forEach(period => {
        const journeyTimes = [];

        carriagewayLinks.forEach(link => {
            journeyTimes.push(
                period.journeyTimes.find(
                    journeyTime => journeyTime.linkId === link
                ) || {
                    linkId: link,
                    journeyTimeSeconds: "N/A",
                    delayBand: {
                        id: 0
                    }
                }
            );
        });
        period.journeyTimes = journeyTimes;
    });

    return carriagewayData;
}

/**
 * @description Adds new journeytimes to the existing ones.
 * @export
 * @param {Object} newDatePeriods - The new data.
 * @param {array} existingDatesPeriods - The existing data.
 * @param {array} carriageways - The existing carriageways.
 * @returns {void}
 */
export function addNewDatePeriods(
    newDatePeriods,
    existingDatesPeriods,
    carriageways
) {
    return new Promise(resolve => {
        addNewDate(
            newDatePeriods.date || getDateStringFromDate(new Date()),
            existingDatesPeriods,
            carriageways
        ).then(datesPeriods => {
            let existingDatePeriods = datesPeriods.find(
                date => date.date === newDatePeriods.date
            );
    
            newDatePeriods.carriageways.forEach(carriageway => {
                const carriagewayData = carriageways.find(
                        c => c.id === carriageway.id
                    ),
                    filledCarriageway = fillTimeBinGaps(
                        carriageway,
                        carriagewayData ? carriagewayData.links : []
                    );
    
                addNewPeriodToCarriageway(filledCarriageway, existingDatePeriods);
            });
            resolve(datesPeriods);
        });        
    });
}

/**
 * @description Adds a new date of empty periods.
 * @export
 * @param {Date} date - The date for which to add period data.
 * @param {array} existingDatesPeriods - an array of existing datesPeriods
 * @param {array} carriageways - an array of carriageways
 * @returns {array} - The updated list of datesPeriods
 */
export function addNewDate(date, existingDatesPeriods, carriageways) {
    const datesPeriods = cloneObject(existingDatesPeriods);

    return new Promise(resolve => {
        let existingDatePeriods = datesPeriods.find(datePeriods => datePeriods.date === date);

        if (existingDatePeriods) {
            resolve(datesPeriods);

            return;
        }

        const periods = generateEmptyPeriods(date);

        datesPeriods.push({
            date: date,
            carriageways: carriageways.map(carriageway => {
                return {
                    id: carriageway.id,
                    periods: periods
                };
            })
        });

        resolve(datesPeriods);
    });
}

/**
 * @description Returns a date in yyyy-mm-dd format
 * @export
 * @param {Object} date - The date to convert.
 * @returns {string} - The formatted date.
 */
export function getDateStringFromDate(date) {
    return `${date.getUTCFullYear()}-${("0" + (date.getUTCMonth() + 1)).slice(
        -2
    )}-${("0" + date.getUTCDate()).slice(-2)}`;
}

/**
 * @description Converts an ISO time string to a timeframe.
 * @export
 * @param {sring} timestring - An ISO time string.
 * @param {number} [timeframeMins=CONSTANTS.UPDATE_INTERVAL_MINS] - The number of minutes in the timeframe
 * @returns {string} - The timeframe.
 */
export function convertTimeBinToTimeframe(
    timestring,
    timeframeMins = CONSTANTS.UPDATE_INTERVAL_MINS
) {
    const date = new Date(timestring),
        startTime = formatTimeAsString(date.getHours(), date.getMinutes());

    date.setMinutes(date.getMinutes() + timeframeMins);
    const endTime = formatTimeAsString(date.getHours(), date.getMinutes());

    return `${startTime} - ${endTime}`;
}

/**
 * @description Sets the junction visibility for a carriageway, using a list of filtered junctions.
 * @param {Object} carriageway - The carriageway.
 * @param {array} filteredJunctions - The list of filtered junctions.
 * @returns {array} A new list of filtered junctions.
 */
function getVisibleJunctions(carriageway, filteredJunctions) {
    return carriageway.junctions
        .filter(junction => {
            return filteredJunctions.indexOf(junction.label) >= 0;
        })
        .map(junction => {
            return {
                ...junction,
                carriageway: carriageway.id
            };
        });
}

/**
 * @description Sets the link visibility for a carriageway.
 * @param {Object} carriageway - The carriageway.
 * @returns  {array} A new list of filtered links.
 */
function getVisibleLinks(carriageway, visibleJunctions) {
    let startLinkId = null,
        endLinkId = null,
        visible = false;

    visibleJunctions.forEach(junction => {
        endLinkId = junction.linkId;
        startLinkId = !startLinkId ? junction.linkId : startLinkId;
    });
    const links = carriageway.links.map(link => {
        return {
            ...link,
            visible: link.id === startLinkId || link.id === endLinkId
        };
    });

    if (startLinkId !== endLinkId) {
        links.forEach(link => {
            if (link.visible) {
                visible = !visible;
            } else {
                link.visible = visible;
            }
        });
    }

    return links
        .filter(link => link.visible)
        .map(link => {
            return {
                ...link,
                carriageway: carriageway.id
            };
        });
}

/**
 * @description Creates a selection list from a routeOptions object.
 * @export
 * @param {array} routeOptions - An array representing the routes.
 * @returns {Object} - An array of the selections.
 */
export function getCarriagewaySelections(routeOptions) {
    const routes = cloneObject(routeOptions);

    return routes.map(route => {
        return {
            name: route.name,
            isSelected: 1,
            carriageways: route.carriageways.map(carriageway => {
                return {
                    name: carriageway.name,
                    id: carriageway.id,
                    isSelected: true
                };
            })
        };
    });
}

/**
 * @description Sets the carriageway visibility based on the specified selections.
 * @export
 * @param {array} selections - the selections.
 * @param {array} routeOptions - The route options.
 * @returns {array} The route options with updated visibility.
 */
export function setCarriagewaySelections(selections, routeOptions) {
    const route = cloneObject(routeOptions),
        selectionRoute = selections.find(
            selection => selection.name === route.name
        );

    return {
        ...route,
        isSelected: selectionRoute ? selectionRoute.isSelected : CONSTANTS.ROUTE_SELECTION_STATE.deselected,
        carriageways: route.carriageways.map(carriageway => {
            const selectedCarriageway = selectionRoute
                ? selectionRoute.carriageways.find(
                      selectionCarriageway =>
                          carriageway.id === selectionCarriageway.id
                  )
                : null;

            return {
                ...carriageway,
                isSelected:
                    selectedCarriageway && selectedCarriageway.isSelected
            };
        })
    };
}

/**
 * @description A generator function to generate a day of timeframes.
 * @export
 * @param {number} [currentHour=0] - (Optional) define the hour from which to start.
 * @param {number} [currentInterval=0] - (Optional) define the interval from which to start.
 * @param {boolean} [dayLimit=true] - (Optional) flag to state whether to end the generator at the end of a day.
 * @param {*} [updateIntervalMins=CONSTANTS.UPDATE_INTERVAL_MINS] - (Optional) the interval ength in mins.
 */
export function* timeframeGenerator(
    currentHour = 0,
    currentInterval = 0,
    dayLimit = true,
    updateIntervalMins = CONSTANTS.UPDATE_INTERVAL_MINS
) {
    const intervals = [];

    for (
        let intervalValue = 0;
        intervalValue < 60;
        intervalValue += updateIntervalMins
    ) {
        intervals.push(("0" + intervalValue).slice(-2));
    }

    while (true) {
        let previousTime = formatTimeAsString(
            currentHour,
            intervals[currentInterval]
        );

        currentInterval++;

        if (currentInterval >= intervals.length) {
            currentInterval = 0;
            currentHour++;

            if (currentHour >= 24) {
                currentHour = 0;

                if (dayLimit) {
                    yield `${previousTime} - ${formatTimeAsString(
                        currentHour,
                        intervals[currentInterval]
                    )}`;
                    break;
                }
            }
        }

        yield `${previousTime} - ${formatTimeAsString(
            currentHour,
            intervals[currentInterval]
        )}`;
    }
}

/**
 * @description Formats time as a string.
 * @param {number | string} hours - The hours.
 * @param {number | string} minutes - The minutes.
 * @returns {string} The formatted time.
 */
function formatTimeAsString(hours, minutes) {
    const hoursInt = parseInt(hours, 10),
        hoursFormatted = ("0" + hoursInt).slice(-2),
        minutesInt = parseInt(minutes, 10),
        minutesFormatted = ("0" + minutesInt).slice(-2);

    return `${hoursFormatted}:${minutesFormatted}`;
}

/**
 * @description Deep clones an array or object using JSON stringify/parse.
 * @param {array | Object} existingArray - The existing array
 * @returns {array | Object} - The cloned array/object.
 */
function cloneObject(existingArray) {
    return JSON.parse(JSON.stringify(existingArray));
}
